import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "@/lib/api";
import { PageHead, Btn, Card, Field, Select, MoneyField, ImageUpload, Label, Toggle } from "@/admin/ui";
import { Loader2, Upload, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "guide", label: "Guide" },
  { value: "lesson", label: "Lesson" },
  { value: "template", label: "Template" },
  { value: "art", label: "Art" },
  { value: "print", label: "Print" },
];

const AssetRow = ({ asset, onChange, onRemove, index }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ ...asset, url: data.url, path: data.path, name: asset.name || file.name });
      toast.success("File uploaded");
    } catch { toast.error("Upload failed — very large files may need to be under ~50MB"); }
    setBusy(false);
  };
  return (
    <div className="border border-espresso/15 p-4 mb-3 bg-ivory" data-testid={`craft-asset-${index}`}>
      <div className="flex gap-3 mb-3">
        <div className="flex-1">
          <Label>File name (shown to buyer)</Label>
          <input value={asset.name || ""} onChange={(e) => onChange({ ...asset, name: e.target.value })}
            placeholder="e.g. Ori Wallpaper Pack" data-testid={`craft-asset-name-${index}`}
            className="w-full bg-white border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso" />
        </div>
        <div className="w-40">
          <Label>Type</Label>
          <select value={asset.kind || "file"} onChange={(e) => onChange({ ...asset, kind: e.target.value })}
            data-testid={`craft-asset-kind-${index}`}
            className="w-full bg-white border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso">
            <option value="file">Uploaded file</option>
            <option value="link">External link</option>
          </select>
        </div>
        <button type="button" onClick={onRemove} data-testid={`craft-asset-remove-${index}`}
          className="self-end mb-[1px] p-2.5 text-wine border border-wine hover:bg-wine hover:text-cream transition-colors"><Trash2 size={14} /></button>
      </div>
      {asset.kind === "link" ? (
        <input value={asset.url || ""} onChange={(e) => onChange({ ...asset, url: e.target.value, path: "" })}
          placeholder="https://… (e.g. a video lesson or hosted file)" data-testid={`craft-asset-url-${index}`}
          className="w-full bg-white border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso" />
      ) : (
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => ref.current?.click()} disabled={busy}
            className="flex items-center gap-2 text-[0.66rem] tracking-[0.14em] uppercase border border-espresso px-3 py-2 hover:bg-espresso hover:text-cream transition-colors">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {asset.url ? "Replace file" : "Upload file"}
          </button>
          {asset.url && <span className="text-xs text-sage truncate">Uploaded ✓</span>}
          <input ref={ref} type="file" hidden onChange={(e) => upload(e.target.files[0])} />
        </div>
      )}
    </div>
  );
};

const VideoField = ({ label, hint, value, onChange, testid }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Video uploaded");
    } catch { toast.error("Upload failed — video should be under ~50MB"); }
    setBusy(false);
  };
  return (
    <div className="mb-4">
      <Label>{label}</Label>
      {hint && <p className="text-espresso/40 text-xs mb-2 -mt-1">{hint}</p>}
      <div className="flex items-center gap-3">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={testid}
          className="flex items-center gap-2 text-[0.66rem] tracking-[0.14em] uppercase border border-espresso px-3 py-2 hover:bg-espresso hover:text-cream transition-colors">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {value ? "Replace video" : "Upload video"}
        </button>
        {value && <a href={mediaUrl(value)} target="_blank" rel="noreferrer" className="text-xs text-sage underline" data-testid={`${testid}-preview`}>Preview ✓</a>}
        {value && <button type="button" onClick={() => onChange("")} data-testid={`${testid}-clear`}
          className="p-2 text-wine border border-wine hover:bg-wine hover:text-cream transition-colors"><Trash2 size={14} /></button>}
        <input ref={ref} type="file" accept="video/*" hidden onChange={(e) => upload(e.target.files[0])} />
      </div>
    </div>
  );
};

const CraftEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/admin/crafts/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Not found")); }, [id]);
  const up = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/crafts/${id}`, c);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };

  const del = async () => {
    if (!window.confirm("Delete this craft permanently?")) return;
    try { await api.delete(`/admin/crafts/${id}`); toast.success("Deleted"); nav("/admin/crafts"); }
    catch { toast.error("Could not delete"); }
  };

  if (!c) return <div className="text-espresso/40">Loading…</div>;

  const gallery = c.gallery || [];
  const assets = c.assets || [];
  const tiers = c.tiers || [];
  const setGallery = (g) => up("gallery", g);
  const setAssets = (a) => up("assets", a);
  const setTiers = (t) => up("tiers", t);

  return (
    <div className="max-w-3xl">
      <PageHead title="Edit craft" sub="A digital piece for your /crafts storefront.">
        <Btn variant="ghost" onClick={() => nav("/admin/crafts")}>Back</Btn>
        <Btn onClick={save} disabled={busy} testid="craft-save">{busy ? <Loader2 size={13} className="animate-spin inline" /> : null} Save</Btn>
      </PageHead>

      <Card className="mb-4">
        <Field label="Title" value={c.title} onChange={(v) => up("title", v)} testid="craft-title" />
        <Field label="Short subtitle" value={c.subtitle} onChange={(v) => up("subtitle", v)} testid="craft-subtitle" placeholder="One line shown under the title" />
        <div className="grid grid-cols-2 gap-4">
          <Select label="Category" value={c.category || "guide"} onChange={(v) => up("category", v)} options={CATEGORIES} testid="craft-category" />
          <Select label="Status" value={c.status || "draft"} onChange={(v) => up("status", v)} testid="craft-status"
            options={[{ value: "draft", label: "Draft (hidden)" }, { value: "live", label: "Live (on sale)" }]} />
        </div>
        <Toggle label="Feature on the Crafts home page" value={c.featured} onChange={(v) => up("featured", v)} testid="craft-featured" />
        <div className="grid grid-cols-2 gap-4">
          <MoneyField label="Price (GBP)" value={c.price} onChange={(v) => up("price", v)} placeholder="e.g. 19.00" testid="craft-price" />
          <Field label="Sort order" type="number" value={c.sort_order} onChange={(v) => up("sort_order", v)} testid="craft-sort" />
        </div>
        <Field label="Description" textarea value={c.description} onChange={(v) => up("description", v)} testid="craft-description" placeholder="What is it, what's inside, who it's for." />
      </Card>

      <Card className="mb-4">
        <ImageUpload label="Cover image" value={c.cover_image} onChange={(v) => up("cover_image", v)} testid="craft-cover" />
        <Label>Gallery (optional extra images)</Label>
        {gallery.map((g, i) => (
          <div key={i} className="flex items-center gap-3 mb-2">
            <div className="w-14 h-16 bg-ivory border border-espresso/10 overflow-hidden shrink-0">
              {g ? <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <input value={g} onChange={(e) => setGallery(gallery.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 bg-ivory border border-espresso/20 px-3 py-2 text-sm outline-none focus:border-espresso" />
            <button type="button" onClick={() => setGallery(gallery.filter((_, j) => j !== i))}
              className="p-2 text-wine border border-wine hover:bg-wine hover:text-cream transition-colors"><Trash2 size={14} /></button>
          </div>
        ))}
        <div className="mt-2"><ImageUpload label="" value="" onChange={(v) => v && setGallery([...gallery, v])} testid="craft-gallery-add" /></div>
      </Card>

      <Card className="mb-4">
        <Label>Videos on the product page</Label>
        <p className="text-espresso/40 text-xs mb-3 -mt-1">Upload vertical (9:16) clips. The main video shows first; the card shows below it.</p>
        <VideoField label="Main video" hint="The hero clip at the top of the product page." value={c.video} onChange={(v) => up("video", v)} testid="craft-video" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Main video eyebrow" value={c.video_eyebrow} onChange={(v) => up("video_eyebrow", v)} testid="craft-video-eyebrow" placeholder="Watch the proof" />
          <Field label="Main video heading" value={c.video_heading} onChange={(v) => up("video_heading", v)} testid="craft-video-heading" />
        </div>
        <VideoField label="Second video (card, shown below the main one)" value={c.card_video} onChange={(v) => up("card_video", v)} testid="craft-card-video" />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Card eyebrow" value={c.card_video_eyebrow} onChange={(v) => up("card_video_eyebrow", v)} testid="craft-card-eyebrow" placeholder="See what's inside" />
          <Field label="Card heading" value={c.card_video_heading} onChange={(v) => up("card_video_heading", v)} testid="craft-card-heading" />
        </div>
      </Card>

      {c.featured && (
        <Card className="mb-4">
          <Label>Crafts home hero copy</Label>
          <p className="text-espresso/40 text-xs mb-3 -mt-1">This product is featured, so it's the hero on /crafts. These control the big headline area.</p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Hero eyebrow (small line on top)" value={c.hero_eyebrow} onChange={(v) => up("hero_eyebrow", v)} testid="craft-hero-eyebrow" placeholder="The Digital Muse Method" />
            <Field label="Hero kicker (label above product)" value={c.hero_kicker} onChange={(v) => up("hero_kicker", v)} testid="craft-hero-kicker" placeholder="The flagship method" />
          </div>
          <Field label="Hero headline" value={c.hero_title} onChange={(v) => up("hero_title", v)} testid="craft-hero-title" placeholder="Small Account. Serious Reach." />
        </Card>
      )}

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <Label>Files the buyer receives</Label>
          <button type="button" onClick={() => setAssets([...assets, { id: crypto.randomUUID(), name: "", kind: "file", url: "", path: "" }])}
            data-testid="craft-asset-add"
            className="flex items-center gap-1.5 text-[0.64rem] tracking-[0.14em] uppercase border border-espresso px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors">
            <Plus size={13} /> Add file
          </button>
        </div>
        {assets.length === 0 && <p className="text-espresso/40 text-sm mb-2">Add at least one file or link. Buyers get these instantly after payment.</p>}
        {assets.map((a, i) => (
          <AssetRow key={a.id || i} asset={a} index={i}
            onChange={(next) => setAssets(assets.map((x, j) => j === i ? next : x))}
            onRemove={() => setAssets(assets.filter((_, j) => j !== i))} />
        ))}
      </Card>

      <Card className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <Label>Pricing tiers (each unlocks only its own files)</Label>
          <button type="button" data-testid="tier-add"
            onClick={() => setTiers([...tiers, { id: crypto.randomUUID(), name: "New tier", price: 0, cta: "Get it", badge: "", includes: [], note: "", assets: [] }])}
            className="flex items-center gap-1.5 text-[0.64rem] tracking-[0.14em] uppercase border border-espresso px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors">
            <Plus size={13} /> Add tier
          </button>
        </div>
        {tiers.length === 0 && <p className="text-espresso/40 text-sm mb-2">No tiers — the product sells at the single price above. Add tiers for options like Guide / Guide+Video.</p>}
        {tiers.map((t, ti) => {
          const setTier = (patch) => setTiers(tiers.map((x, j) => j === ti ? { ...x, ...patch } : x));
          const tAssets = t.assets || [];
          return (
            <div key={t.id || ti} data-testid={`tier-${ti}`} className="border border-espresso/20 p-4 mb-4 bg-ivory">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Tier name" value={t.name} onChange={(v) => setTier({ name: v })} testid={`tier-name-${ti}`} />
                <MoneyField label="Price (GBP)" value={t.price} onChange={(v) => setTier({ price: v })} testid={`tier-price-${ti}`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Button text" value={t.cta} onChange={(v) => setTier({ cta: v })} placeholder="Get the guide" testid={`tier-cta-${ti}`} />
                <Field label="Badge (optional)" value={t.badge} onChange={(v) => setTier({ badge: v })} placeholder="Most chosen" testid={`tier-badge-${ti}`} />
              </div>
              <Field label="What's included (one per line)" textarea value={(t.includes || []).join("\n")}
                onChange={(v) => setTier({ includes: v.split("\n").map((s) => s.trim()).filter(Boolean) })} testid={`tier-includes-${ti}`} />
              <Field label="Post-purchase note (optional — shown on thank-you page & email)" value={t.note}
                onChange={(v) => setTier({ note: v })} placeholder="e.g. Your physical starter kit will be shipped separately." testid={`tier-note-${ti}`} />
              <div className="mb-3">
                <Toggle label="Pause this tier — sold out" value={!!t.sold_out} onChange={(v) => setTier({ sold_out: v })} testid={`tier-soldout-${ti}`} />
                <p className="text-xs text-espresso/40 -mt-1">Buyers can't purchase this option (use when a physical kit is out of stock). Your other tiers stay on sale.</p>
              </div>
              <Label>Files unlocked by this tier</Label>
              {tAssets.map((a, ai) => (
                <AssetRow key={a.id || ai} asset={a} index={`${ti}-${ai}`}
                  onChange={(next) => setTier({ assets: tAssets.map((x, j) => j === ai ? next : x) })}
                  onRemove={() => setTier({ assets: tAssets.filter((_, j) => j !== ai) })} />
              ))}
              <div className="flex gap-2 mt-2">
                <button type="button" onClick={() => setTier({ assets: [...tAssets, { id: crypto.randomUUID(), name: "", kind: "file", url: "", path: "" }] })}
                  className="flex items-center gap-1.5 text-[0.62rem] tracking-[0.14em] uppercase border border-espresso px-3 py-1.5 hover:bg-espresso hover:text-cream transition-colors">
                  <Plus size={12} /> Add file
                </button>
                <button type="button" onClick={() => setTiers(tiers.filter((_, j) => j !== ti))} data-testid={`tier-remove-${ti}`}
                  className="text-[0.62rem] tracking-[0.14em] uppercase text-wine border border-wine px-3 py-1.5 hover:bg-wine hover:text-cream transition-colors">
                  Remove tier
                </button>
              </div>
            </div>
          );
        })}
      </Card>

      <div className="flex justify-between">
        <Btn variant="danger" onClick={del} testid="craft-delete">Delete</Btn>
        <Btn onClick={save} disabled={busy} testid="craft-save-bottom">{busy ? <Loader2 size={13} className="animate-spin inline" /> : null} Save</Btn>
      </div>
    </div>
  );
};

export default CraftEditor;
